<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { FormElement, TextInputProperties } from '../../../model/form.types';
import { useLabelPosition } from '../../../model/useLabelPosition';
import { useElementContainerStyles } from '../../../model/useElementContainerStyles';
import { useFormBuilderStore } from '../../../model/form-builder.store';
import { useEditableField } from '../../../model/useFormBuilderReadonly';

interface Props {
  element: FormElement & { properties: TextInputProperties };
}

const props = defineProps<Props>();
const formBuilderStore = useFormBuilderStore();

const inputValue = ref(props.element.properties.value ?? '');

watch(
  () => props.element.properties.value,
  (newValue) => {
    inputValue.value = newValue ?? '';
  }
);

const { isLabelLeft, inputClasses, labelClasses, labelStyles } = useLabelPosition(
  computed(() => props.element.properties)
);

const { containerStyles } = useElementContainerStyles(
  computed(() => props.element.properties),
  'TEXT_INPUT'
);

const isEditable = useEditableField(() => props.element.properties.editable);

function persistValue(value: string) {
  if (isEditable.value) {
    formBuilderStore.updateCellContentProperty(props.element.id, { value });
  }
}

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  const newValue = target.value;
  const regex = props.element.properties.regex;

  if (!regex) {
    inputValue.value = newValue;
    persistValue(newValue);
    return;
  }

  try {
    const pattern = new RegExp(regex);

    if (newValue === '' || pattern.test(newValue)) {
      inputValue.value = newValue;
      persistValue(newValue);
    } else {
      target.value = inputValue.value;
    }
  } catch {
    inputValue.value = newValue;
    persistValue(newValue);
  }
}
</script>

<template>
  <div class="text-input-element" :class="{ 'label-left': isLabelLeft }" :style="containerStyles">
    <label :for="`input-${element.id}`" :class="labelClasses" :style="labelStyles">
      {{ element.properties.label }}
    </label>
    <input :id="`input-${element.id}`" :value="inputValue" type="text" :name="element.properties.name"
      :placeholder="element.properties.placeholder" :maxlength="element.properties.maxLength"
      :pattern="element.properties.regex" :required="element.properties.required"
      :aria-label="element.properties.label || element.properties.name" :readonly="!isEditable" :class="inputClasses"
      @input="handleInput">
  </div>
</template>

<style scoped>
.text-input-element {
  font-family: var(--font-base);
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.text-input-element.label-left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.text-input-element.label-left label {
  max-width: 180px;
}

.text-input-element.label-left input {
  flex: 1;
  min-width: 42px;
  max-width: 100%;
  width: 0;
  /* Force flex item to respect container width */
}

input {
  font-size: 11px;
  line-height: 16px;
}

input::placeholder {
  font-size: 10px;
  opacity: 0.6;
}
</style>
